import React from 'react';
import { FiCheck, FiClock, FiGlobe, FiMapPin, FiUser } from 'react-icons/fi';

function BookingChatMessage({
  language,
  message,
  isActive,
  draftSelections,
  onOptionSelect,
  onToggleChip,
  onCommitMultiSelect,
  onProviderPreview,
  onProviderSelect,
  onSlotSelect,
  onSummaryAction,
  formatProviderList,
  renderProviderImage,
  localizeProviderValue,
  caregiverTier
}) {
  const isAssistant = message.role === 'assistant';

  function renderInteractiveBody() {
    if (message.kind === 'options' || message.kind === 'cards') {
      return (
        <div className={`booking-chat-message__options ${message.kind === 'cards' ? 'is-cards' : ''}`}>
          {(message.options || []).map((option) => (
            <button
              type="button"
              key={option.value}
              className="booking-chat-message__option"
              onClick={() => onOptionSelect(message.promptKey, option.value)}
              disabled={!isActive}
            >
              {option.label}
            </button>
          ))}
        </div>
      );
    }

    if (message.kind === 'multi_select') {
      const selectedValues = draftSelections[message.promptKey] || [];
      return (
        <div className="booking-chat-message__multi-select">
          <div className="booking-chat-message__chips">
            {(message.options || []).map((option) => {
              const active = selectedValues.includes(option.value);
              return (
                <button
                  type="button"
                  key={option.value}
                  className={`booking-chat-message__chip ${active ? 'is-active' : ''}`}
                  onClick={() => onToggleChip(message.promptKey, option.value)}
                  disabled={!isActive}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="booking-chat-message__confirm"
            onClick={() => onCommitMultiSelect(message.promptKey)}
            disabled={!isActive || selectedValues.length === 0}
          >
            {message.submitLabel || (language === 'fr' ? 'Confirmer' : 'Confirm')}
          </button>
        </div>
      );
    }

    if (message.kind === 'providers') {
      return (
        <div className="booking-chat-message__providers">
          {(message.providers || []).map((provider) => (
            <article className="booking-chat-message__provider-card" key={provider.provider_id}>
              <div className="booking-chat-message__provider-head">
                {renderProviderImage(provider)}
                <div>
                  <p className="booking-chat-message__provider-kicker">
                    {localizeProviderValue(provider.speciality_label || provider.specialities?.[0]?.name || '', language)}
                  </p>
                  <h4>{provider.first_name} {provider.last_name}</h4>
                  <span className="booking-chat-message__provider-verified">
                    <FiCheck />
                    {language === 'fr' ? 'Vérifié' : 'Verified'}
                  </span>
                </div>
              </div>
              <div className="booking-chat-message__provider-meta">
                <span><FiGlobe /> {formatProviderList(provider.languages).join(', ') || 'N/A'}</span>
                <span><FiMapPin /> {provider.zone_name || provider.zone_id || 'Montreal'}</span>
                <span><FiUser /> {provider.years_experience ? `${provider.years_experience} ${language === 'fr' ? 'ans' : 'years'}` : (language === 'fr' ? 'Expérience non indiquée' : 'Experience not listed')}</span>
              </div>
              <div className="booking-chat-message__provider-services">
                {(provider.matchedServices || []).slice(0, 3).map((service) => (
                  <button
                    type="button"
                    key={service.service_id}
                    className="booking-chat-message__service-card"
                    onClick={() => onProviderSelect(provider, service)}
                    disabled={!isActive}
                  >
                    <span>{localizeProviderValue(service.name, language)}</span>
                    <strong>
                      {message.route === 'caregiver'
                        ? caregiverTier === 'heavy' ? '$30/h' : '$25/h'
                        : `${(service.price_cents / 100).toFixed(2)} + 8%`}
                    </strong>
                  </button>
                ))}
              </div>
              <button type="button" className="booking-chat-message__secondary" onClick={() => onProviderPreview(provider)}>
                {language === 'fr' ? 'Voir le profil' : 'View profile'}
              </button>
            </article>
          ))}
        </div>
      );
    }

    if (message.kind === 'slots') {
      return (
        <div className="booking-chat-message__slots">
          {(message.slots || []).map((slot) => (
            <button
              type="button"
              key={slot.id}
              className="booking-chat-message__slot"
              onClick={() => onSlotSelect(slot)}
              disabled={!isActive}
            >
              <span>{slot.label}</span>
              <strong><FiClock /> {slot.mode}</strong>
            </button>
          ))}
        </div>
      );
    }

    if (message.kind === 'summary') {
      return (
        <div className="booking-chat-message__summary">
          <div className="booking-chat-message__summary-items">
            {(message.items || []).map((item) => (
              <div className="booking-chat-message__summary-row" key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
          <div className="booking-chat-message__summary-actions">
            {(message.actions || []).map((action) => (
              <button
                type="button"
                key={action.value}
                className={action.primary ? 'booking-chat-message__confirm' : 'booking-chat-message__secondary'}
                onClick={() => onSummaryAction(action.value)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (message.kind === 'checkout') {
      return (
        <div className="booking-chat-message__summary-actions">
          {(message.actions || []).map((action) => (
            <button
              type="button"
              key={action.value}
              className={action.primary ? 'booking-chat-message__confirm' : 'booking-chat-message__secondary'}
              onClick={() => onSummaryAction(action.value)}
            >
              {action.label}
            </button>
          ))}
        </div>
      );
    }

    if (message.kind === 'example') {
      return (
        <div className="booking-chat-message__example">
          {(message.items || []).map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      );
    }

    return null;
  }

  return (
    <article className={`booking-chat-message ${isAssistant ? 'is-assistant' : 'is-user'}`}>
      <div className="booking-chat-message__bubble">
        {message.text ? <p>{message.text}</p> : null}
        {renderInteractiveBody()}
      </div>
    </article>
  );
}

export default BookingChatMessage;
